import { useState, type ReactNode } from 'react';
import { LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

import StateMark from '../dossier/StateMark';
import type { AreaGroup, ManifestRow } from '../setupState';
import {
    MARK_WORDS,
    groupCounter,
    isGroupOpen,
    markFor,
    markerHome,
    rowMeta,
    type MarkKind,
} from './manifestView';

/**
 * IT TAKES THE GROUPS, NOT THE STATE — a narrowing, and the whole of what this column reads.
 *
 * `SetupState` was the prop and `state.areaGroups` was the only member ever touched: the rows, the
 * progress, the narrator and the activation gate all travelled here to be ignored. Narrowing it to
 * the one member is what lets the advance dossier — whose derivation has no `ActivationGate` and
 * never will — draw this same column, and it costs the existing call sites nothing (they pass a
 * `SetupState`, which has the member). The two parameters carry WHICH area keys and WHICH row ids
 * this particular rail is made of, so a page cannot hand the column one rail's groups and the
 * other's selected row.
 */
interface Props<Area extends string, Id extends string> {
    state: { areaGroups: AreaGroup<Area, Id>[] };
    /** The area the canvas is showing. Its group is always expanded. */
    activeArea: Area | null;
    activeRowId: Id | null;
    onSelectRow: (row: ManifestRow<Area, Id>) => void;
    /** The pinned activation control. */
    footer: ReactNode;
    /**
     * The page's identity block — title and narrator — as the head of this column's SCROLLER,
     * not as a band above it. The name of the contract scrolls away with the groups it belongs
     * to, and only the activation control below stays put. Never passed to the sheet: the
     * drawer's own title says what it is, and a second `aria-live` sentence in the tree would
     * announce every state change twice.
     */
    head?: ReactNode;
    /** `sheet` drops the column geometry: below 1024px this column lives in a vaul drawer. */
    variant?: 'column' | 'sheet';
}

/**
 * Row classes as LITERALS, never composed.
 *
 * Tailwind scans source text for class names and drops component-layer rules nothing mentions,
 * so a `` `sd-row-${mark}` `` template would leave these rules to survive only by accident of
 * appearing beside a class that is written out somewhere else.
 */
const ROW_CLASS: Record<MarkKind, string> = {
    move: 'sd-row-move',
    wait: 'sd-row-wait',
    locked: 'sd-row-locked',
    done: 'sd-row-done',
    record: 'sd-row-record',
    draft: 'sd-row-draft',
    refused: 'sd-row-refused',
};

/** The 180ms `--ease-in` the rest of the column moves on, as a layout transition. */
const MARKER_MOTION = { duration: 0.18, ease: [0.2, 0, 0, 1] } as const;

/**
 * The manifest — every requirement in the journey, in one column, from the one derivation.
 *
 * THIS IS THE ACTIVATION PREFLIGHT, NOT A NAV RAIL. The rows are `computeSetupState`'s rows and
 * the footer gates on `computeSetupState`'s gate, so the classic wizard failure — seven steps
 * ticked and a server that still refuses — cannot be expressed here: there is one list and the
 * server's own gates are eight of its members.
 *
 * The mitigation for its one real risk ships with it. Twenty-one rows would be noise, so a
 * satisfied group collapses to a single dated receipt and a settled contract shows six lines;
 * the group holding the current route stays open regardless, and an explicit toggle wins over
 * both. Receipts carry WHEN, which is what makes a three-day return reconstruct history rather
 * than just state.
 */
export default function Manifest<Area extends string, Id extends string>({
    state,
    activeArea,
    activeRowId,
    onSelectRow,
    footer,
    head,
    variant = 'column',
}: Props<Area, Id>) {
    const [overrides, setOverrides] = useState<Partial<Record<Area, boolean>>>({});
    const reduceMotion = useReducedMotion();

    /**
     * The 2px brand marker: on the selected row, and on the current area's header until a row is
     * picked — so an admin who arrives by route still sees where they are standing.
     *
     * IT IS A CHILD OF THE THING IT MARKS, chosen by `markerHome` from the same key comparison the
     * expansion uses. It was a single absolutely positioned bar in the scroller, placed from a
     * measured `offsetTop`; that arrangement had two origins — `offsetTop` counts from the
     * scroller's padding edge, an absolute box with `top: auto` starts at its own static position —
     * and the moment the identity block moved into the scroller between the two, every route paid
     * that block's height twice and the marker named a group two below the one it meant. Rendered
     * where it belongs there is no offset left to be wrong: the host's own box IS the position, and
     * a row that grows a third line grows its marker with it.
     *
     * `layoutId` is what keeps it moving BETWEEN groups — one element handed from head to head, so
     * the eye follows the change rather than discovering it. It is namespaced per `variant`: the
     * column and the sheet both render a manifest, and a shared id across the two would fly the
     * marker between a drawer and the page behind it. Reduced motion collapses the slide to an
     * arrival, which is the same rule the stylesheet applies to everything else on this page.
     */
    const marker = (
        <motion.span
            layoutId="sd-marker"
            className="sd-marker"
            aria-hidden="true"
            transition={reduceMotion ? { duration: 0 } : MARKER_MOTION}
        />
    );

    return (
        /*
         * A PLAIN DIV, NOT AN `aside`. This element is the page's whole left column now — the
         * contract's name and the live sentence come through `head` and scroll with the groups —
         * and an `h1` inside a complementary landmark is a page whose title belongs to its
         * sidebar. The landmark moved down onto the group list, which is the part that actually
         * is the manifest.
         */
        <div className={cn('sd-manifest', variant === 'sheet' && 'sd-manifest-sheet')}>
            {/*
              * ONE SCROLLER FOR THE WHOLE COLUMN, and the identity block is inside it. Only the
              * activation control below is pinned.
              *
              * NO STANDING HEAD IN IT EITHER. The column used to open with the word `Journey`
              * over `N of 6 areas complete · N items need you · N waiting on others` — a label
              * naming the thing it sits on top of, and a progress line every row underneath it
              * already says in the specific. The count survives where it is the only thing a
              * control can say, on the sheet trigger below 1024px.
              */}
            <div className="sd-manifest-scroll scrollbar-thin">
                {head}

                <LayoutGroup id={variant}>
                <nav aria-label="Journey manifest">
                {state.areaGroups.map((group) => {
                    const open = isGroupOpen(group, activeArea, overrides);
                    const home = markerHome(group, activeArea, activeRowId, open);
                    /**
                     * The receipt REPLACES the counter on a collapsed satisfied group, per A2 —
                     * printing `1/1` beside `Deposit received May 11, 2026` is the same fact
                     * twice, and two right-aligned annotations in a 340px header is most of what
                     * makes the column read as cluttered.
                     */
                    const receipt = !open && group.receipt ? group.receipt : null;
                    return (
                        <div key={group.area} className="sd-group">
                            <button
                                type="button"
                                data-group={group.area}
                                className="sd-group-head"
                                aria-expanded={open}
                                onClick={() =>
                                    setOverrides((current) => ({ ...current, [group.area]: !open }))
                                }
                            >
                                {home === 'head' && marker}
                                <span className="sd-g-index">{group.index}</span>
                                <span className="sd-g-name">{group.label}</span>
                                {receipt ? (
                                    <span className="sd-g-receipt" title={receipt}>
                                        {receipt}
                                    </span>
                                ) : (
                                    <span className="sd-g-counter">{groupCounter(group)}</span>
                                )}
                                <ChevronRight className="sd-g-chev h-3 w-3" />
                            </button>

                            {open && (
                                <div>
                                    {group.rows.map((row) => {
                                        const mark = markFor(row);
                                        const meta = rowMeta(row);
                                        return (
                                            <button
                                                key={row.id}
                                                type="button"
                                                data-row={row.id}
                                                aria-current={row.id === activeRowId ? 'true' : undefined}
                                                className={cn('sd-row', ROW_CLASS[mark])}
                                                onClick={() => onSelectRow(row)}
                                            >
                                                {home === 'row' && row.id === activeRowId && marker}
                                                <StateMark kind={mark} />
                                                {/*
                                                  * Both lines carry `title`: they are single-line
                                                  * and elided, and a value an admin cannot finish
                                                  * reading has to be recoverable without leaving
                                                  * the column.
                                                  *
                                                  * NO ROW WEARS THE WORD "optional" — owner's
                                                  * ruling — and none is dormant either since
                                                  * 2026-08-04. `row.optional` survives as the
                                                  * DORMANCY flag the counters read, with nothing
                                                  * declaring it: the deposit and insurance-loan
                                                  * agreements are required on every contract, so
                                                  * each is counted and drawn blue until it is
                                                  * papered, and its note names the order its chain
                                                  * is done in rather than a badge that reads as
                                                  * "you may skip this".
                                                  */}
                                                <span className="sd-r-label" title={row.label}>
                                                    {row.label}
                                                    <span className="sr-only"> — {MARK_WORDS[mark]}</span>
                                                </span>
                                                <span className="sd-r-meta" title={meta}>
                                                    {meta}
                                                </span>
                                                {row.note && (
                                                    <span className="sd-r-note">
                                                        {row.note}
                                                        {row.refusal && <span className="sd-fix">Fix</span>}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
                </nav>
                </LayoutGroup>
            </div>

            <div className="sd-manifest-foot">{footer}</div>
        </div>
    );
}
